import { markGoogleUser } from '../utils/googleHint';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import authApi from '../services/authApi';
import i18n from '../i18n/config';
import { canSignIn } from '../config/loginRoles';

// The server opens a session for any valid account, whatever its role. Only the roles in config/loginRoles may use the web
// app, so a refused account is never stored: the returned error is thrown by the caller, and the session the server just
// opened with `token` is closed again (best effort — the token is dropped either way).
const refuseRole = (token) => {
    if (token) authApi.logout(token).catch(() => {});
    const error = new Error(i18n.t('auth.roleNotAllowed'));
    error.code = 'ROLE_NOT_ALLOWED'; // no `status`: LoginPage must not count this as a wrong-password attempt
    return error;
};

// Monotonic counter bumped on every local write to `user` (profile/settings save).
// getCurrentUser() reads it to avoid a slow, in-flight `/api/auth/me` refresh
// overwriting a save that completed while that GET was still on the wire. Without
// this, saving settings right after page load intermittently "doesn't stick" until
// a reload or a 2nd/3rd click, because the mount-time /me response lands last and
// reverts the store to pre-save data.
let userWriteSeq = 0;

export const useAuthStore = create(
    persist(
        (set, get) => ({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,

            // Login
            login: async (credentials) => {
                set({ isLoading: true });
                try {
                    const response = await authApi.login(credentials);
                    const { user, token } = response.data;
                    if (!canSignIn(user?.role)) throw refuseRole(token);

                    set({
                        user,
                        token,
                        isAuthenticated: true,
                        isLoading: false,
                    });

                    return { success: true, user };
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            // Verify OTP and log in (unverified user from login page)
            verifyAndLogin: async (phone, otp, purpose = 'registration') => {
                set({ isLoading: true });
                try {
                    const response = await authApi.verifyAndLogin({ phone, otp, purpose });
                    const { user, token } = response.data;
                    if (!canSignIn(user?.role)) throw refuseRole(token);
                    set({ user, token, isAuthenticated: true, isLoading: false });
                    return { success: true, user };
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            // Pre-register: save unverified user + send OTP
            preRegister: async (userData) => {
                set({ isLoading: true });
                try {
                    const response = await authApi.preRegister(userData);
                    set({ isLoading: false });
                    return response.data;
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            // [TEMPORARY - OTP BYPASS] Direct register without phone verification.
            // To re-enable OTP: remove this action and restore preRegister + OTP step in RegisterPage.
            directRegister: async (userData) => {
                set({ isLoading: true });
                try {
                    const response = await authApi.directRegister(userData);
                    const { user, token } = response.data;
                    if (!canSignIn(user?.role)) throw refuseRole(token);
                    set({ user, token, isAuthenticated: true, isLoading: false });
                    return { success: true, user };
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            // Continue with Google (sign up or sign in) — `data` = { accessToken, country?, referralCode? }
            googleAuth: async (data) => {
                set({ isLoading: true });
                try {
                    const response = await authApi.googleAuth(data);
                    const { user, token } = response.data;
                    if (!canSignIn(user?.role)) throw refuseRole(token);
                    markGoogleUser(); // this browser used Google: the login page keeps offering it
                    set({ user, token, isAuthenticated: true, isLoading: false });
                    return { success: true, user, isNew: !!user?.isNew };
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            // Change phone number during registration
            changeRegistrationPhone: async (oldPhone, newPhone) => {
                set({ isLoading: true });
                try {
                    const response = await authApi.changeRegistrationPhone({ oldPhone, newPhone });
                    set({ isLoading: false });
                    return response.data;
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            // Send OTP via email during registration
            sendRegistrationEmailOtp: async (phone, email) => {
                set({ isLoading: true });
                try {
                    const response = await authApi.sendRegistrationEmailOtp({ phone, email });
                    set({ isLoading: false });
                    return response.data;
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            // Send OTP
            sendOtp: async (phone, purpose = 'registration') => {
                set({ isLoading: true });
                try {
                    const response = await authApi.sendOtp({ phone, purpose });
                    set({ isLoading: false });
                    return response.data;
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            // Verify OTP. `identifier` is a phone string (registration/login) or an
            // { phone } / { email } object (password reset — non-BD users pass email).
            verifyOtp: async (identifier, otp, purpose = 'registration') => {
                set({ isLoading: true });
                try {
                    const idObj = typeof identifier === 'string' ? { phone: identifier } : (identifier || {});
                    const response = await authApi.verifyOtp({ ...idObj, otp, purpose });
                    set({ isLoading: false });
                    return response.data;
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            // Complete registration after OTP verification
            completeRegistration: async (userData) => {
                set({ isLoading: true });
                try {
                    const response = await authApi.completeRegistration(userData);
                    const { user, token } = response.data;
                    if (!canSignIn(user?.role)) throw refuseRole(token);

                    set({
                        user,
                        token,
                        isAuthenticated: true,
                        isLoading: false,
                    });

                    return { success: true, user };
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            // Forgot password. `identifier` is a phone string or an { phone } / { email }
            // object; the backend routes the OTP by the account's country (SMS vs email)
            // and returns { channel, maskedTarget }.
            forgotPassword: async (identifier) => {
                set({ isLoading: true });
                try {
                    const idObj = typeof identifier === 'string' ? { phone: identifier } : (identifier || {});
                    const response = await authApi.forgotPassword(idObj);
                    set({ isLoading: false });
                    return response.data;
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            // Reset password. `identifier` is a phone string or an { phone } / { email } object.
            resetPassword: async (identifier, otp, newPassword) => {
                set({ isLoading: true });
                try {
                    const idObj = typeof identifier === 'string' ? { phone: identifier } : (identifier || {});
                    const response = await authApi.resetPassword({
                        ...idObj,
                        otp,
                        newPassword,
                        purpose: 'password_reset'
                    });
                    set({ isLoading: false });
                    return response.data;
                } catch (error) {
                    set({ isLoading: false });
                    throw error;
                }
            },

            // Logout
            logout: () => {
                set({
                    user: null,
                    token: null,
                    isAuthenticated: false,
                });
            },

            // Get current user
            getCurrentUser: async () => {
                const seqAtStart = userWriteSeq;
                try {
                    const response = await authApi.getCurrentUser();

                    // A profile/settings save landed while this refresh was in flight.
                    // Its data is newer than what the server sent back to us, so keep the
                    // just-saved state instead of clobbering it with pre-save data.
                    if (userWriteSeq !== seqAtStart) {
                        return get().user;
                    }

                    const user = response.data;
                    // The account's role changed to one the web app refuses while it was signed in: end the session.
                    // (Thrown inside the try, so the catch below clears the store.)
                    if (!canSignIn(user?.role)) throw refuseRole(get().token);
                    set({ user });
                    return user;
                } catch (error) {
                    // Only the server's refusal ends the session. A dropped connection, a server restart or a 5xx / 429 says nothing
                    // about the login, so the user stays signed in and the next refresh can succeed.
                    const refused = error?.code === 'ROLE_NOT_ALLOWED'
                        || (error?.status >= 400 && error.status < 500 && error.status !== 429);
                    if (refused) get().logout();
                    throw error;
                }
            },

            // Update profile
            updateProfile: async (profileData) => {
                try {
                    const response = await authApi.updateProfile(profileData);
                    const user = response.data;

                    userWriteSeq++;
                    set({ user });
                    return user;
                } catch (error) {
                    throw error;
                }
            },

            // Update user settings (UI preferences)
            updateSettings: async (newSettings) => {
                const { user } = get();
                const merged = { ...(user?.settings || {}), ...newSettings };
                try {
                    const response = await authApi.updateProfile({ settings: merged });
                    const updatedUser = response.data;
                    userWriteSeq++;
                    set({ user: updatedUser });
                    return updatedUser;
                } catch (error) {
                    throw error;
                }
            },

            // Check if user has a specific role
            hasRole: (role) => {
                const { user } = get();
                return user?.role === role;
            },

            // Check if user is admin
            isAdmin: () => {
                const { user } = get();
                return user?.role === 'admin';
            },
        }),
        {
            name: 'auth-storage',
            partialize: (state) => ({
                user: state.user,
                token: state.token,
                isAuthenticated: state.isAuthenticated,
            }),
            // A session saved earlier by a role the web app refuses (e.g. a `user` or `superadmin` login from before the
            // allowlist) must not come back to life when the app loads: start signed out instead.
            merge: (persisted, current) => (persisted?.user && !canSignIn(persisted.user.role) ? current : { ...current, ...persisted }),
        }
    )
);
